import { useQueryClient, useMutation } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import config from 'src/config';
import ApiError from 'src/api/ApiError';
import useGetApi from 'src/api/useGetApi';
import useMutationApi from 'src/api/useMutationApi';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';

export interface ManualAmount {
  id: string;
  ruleId: string;
  ruleName: string;
  divisionId: string;
  divisionName: string;
  amount: string; // DECIMAL field returned as string by Sequelize
  date: Date;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateManualAmountRequest {
  ruleId: string;
  divisionId: string;
  amount: number;
  date: string; // ISO date string
  description: string;
}

export interface UpdateManualAmountRequest {
  amount?: number;
  date?: string; // ISO date string
  description?: string;
}

// Get manual amounts by agreement
export const useManualAmountsByAgreement = (
  agreementId: string,
  startDate?: string,
  endDate?: string,
  enabled = true,
) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  return useGetApi<ManualAmount[]>({
    endpoint: `rebates/manual-amounts/agreement/${agreementId}?${params.toString()}`,
    options: {
      enabled: enabled && !!agreementId,
    },
  });
};

// Get manual amounts by rule
export const useManualAmountsByRule = (
  ruleId: string,
  startDate?: string,
  endDate?: string,
  enabled = true,
) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  return useGetApi<ManualAmount[]>({
    endpoint: `rebates/manual-amounts/rule/${ruleId}?${params.toString()}`,
    options: {
      enabled: enabled && !!ruleId,
    },
  });
};

// Get single manual amount
export const useManualAmount = (id: string, enabled = true) => {
  return useGetApi<ManualAmount>({
    endpoint: `rebates/manual-amounts/${id}`,
    options: {
      enabled: enabled && !!id,
    },
  });
};

// Create manual amount
export const useCreateManualAmount = (agreementId: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<CreateManualAmountRequest, ManualAmount>({
    endpoint: 'rebates/manual-amounts',
    method: 'POST',
    successMessage: 'Manual amount created successfully',
    options: {
      onSuccess: () => {
        // Invalidate all manual amounts queries for this agreement
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/manual-amounts'),
        });
        // Invalidate forecast so it regenerates with the new manual amount
        queryClient.invalidateQueries({
          queryKey: orpc.rebates.forecast.generateForAgreement.queryKey({
            input: { agreementId },
          }),
        });
      },
    },
  });
};

// Update manual amount
export const useUpdateManualAmount = (id: string, agreementId: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<UpdateManualAmountRequest, ManualAmount>({
    endpoint: `rebates/manual-amounts/${id}`,
    method: 'PUT',
    successMessage: 'Manual amount updated successfully',
    options: {
      onSuccess: () => {
        // Invalidate all manual amounts queries for this agreement
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/manual-amounts'),
        });
        // Invalidate forecast so it regenerates with the updated manual amount
        queryClient.invalidateQueries({
          queryKey: orpc.rebates.forecast.generateForAgreement.queryKey({
            input: { agreementId },
          }),
        });
      },
    },
  });
};

// Delete manual amount — takes id as a mutation variable so the endpoint
// isn't baked in at hook-init time (avoids 404 when id is not yet known).
export const useDeleteManualAmount = (agreementId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string }>({
    mutationFn: async ({ id }) => {
      const response = await fetch(
        `${config.apiBaseUrl}/rebates/manual-amounts/${id}`,
        {
          method: 'DELETE',
          headers: buildPosthogHeaders({
            'Content-Type': 'application/json',
          }),
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorData: { message?: string } = await response
          .json()
          .catch(() => ({}));
        throw new ApiError(
          response.status,
          errorData.message || 'Failed to delete manual amount',
        );
      }
    },
    onSuccess: () => {
      // Invalidate all manual amounts queries for this agreement
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].includes('rebates/manual-amounts'),
      });
      // Invalidate forecast so it regenerates without the deleted manual amount
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.forecast.generateForAgreement.queryKey({
          input: { agreementId },
        }),
      });
    },
  });
};

export default useManualAmountsByAgreement;
